import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { BetaAccessGate } from "@/components/beta-access-gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText, Send, CheckCircle, Trash2, RefreshCw, Download, Plus, DollarSign, AlertCircle, Bell, BellOff,
} from "lucide-react";
import type { OwnerInvoice, Owner, OwnerInvoiceLineItem, OwnerInvoiceConfig, PmFeeSettings } from "@shared/schema";
import { groupOwnerInvoiceLineItems } from "@shared/owner-invoice-render";
import { downloadOwnerInvoicePdf } from "@/lib/owner-invoice-pdf";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const STATUS_META: Record<string, { label: string; className: string }> = {
  draft:   { label: "Draft",   className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  sent:    { label: "Sent",    className: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  viewed:  { label: "Viewed",  className: "bg-indigo-100 text-indigo-800" },
  paid:    { label: "Paid",    className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  overdue: { label: "Overdue", className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
};

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

function billingMonthLabel(bm: string): string {
  const [y, m] = bm.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
}

function prevBillingMonth(): string {
  const now = new Date();
  const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  return `${prevYear}-${String(prevMonth).padStart(2, "0")}`;
}

type Tab = "all" | "draft" | "sent" | "paid" | "overdue";

export default function AppOperationsOwnerInvoices() {
  usePageTitle("Owner Invoices");
  const { canEdit } = usePermissions();
  const { toast } = useToast();
  const readOnly = !canEdit("owners");

  const [tab, setTab] = useState<Tab>("all");
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(prevBillingMonth);
  const [generateOwnerId, setGenerateOwnerId] = useState<string>("all");
  const [detailInvoice, setDetailInvoice] = useState<OwnerInvoice | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendInvoice, setSendInvoice] = useState<OwnerInvoice | null>(null);
  const [sendMessage, setSendMessage] = useState("");
  const [sendUseFallback, setSendUseFallback] = useState(false);
  const [notesEdit, setNotesEdit] = useState("");

  const invoicesQuery = useQuery<OwnerInvoice[]>({
    queryKey: ["/api/app/operations/owner-invoices"],
  });

  const ownersQuery = useQuery<{ owners: Owner[] }>({
    queryKey: ["/api/app/operations/owners"],
  });

  // Invoice identity (company name/address/logo/footer) configured on the
  // Billing & Fees page; renders as the header of the preview and PDF.
  const feeSettingsQuery = useQuery<PmFeeSettings | null>({
    queryKey: ["/api/app/account/pm-fee-settings"],
  });
  const invoiceConfig: OwnerInvoiceConfig | null = feeSettingsQuery.data?.ownerInvoiceConfig ?? null;

  const owners = ownersQuery.data?.owners ?? [];

  const filtered = (invoicesQuery.data ?? []).filter((inv) => {
    if (tab === "all") return true;
    return inv.status === tab;
  });

  const generateMutation = useMutation({
    mutationFn: async ({ billingMonth, ownerId }: { billingMonth: string; ownerId?: number }) => {
      const r = await apiRequest("POST", "/api/app/operations/owner-invoices/generate", {
        billingMonth,
        ...(ownerId ? { ownerId } : {}),
      });
      return r.json();
    },
    onSuccess: (data: { created: number; updated: number; skipped: number; errors: string[] }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owner-invoices"] });
      setGenerateOpen(false);
      const { created, updated, skipped, errors } = data;
      if (errors?.length > 0) {
        toast({ title: "Generated with errors", description: errors.slice(0, 2).join("; "), variant: "destructive" });
      } else {
        toast({ title: `Invoices generated`, description: `${created} created, ${updated} updated, ${skipped} skipped.` });
      }
    },
    onError: (e: unknown) => toast({ title: "Generate failed", description: parseErrorMessage(e, "Could not generate invoices."), variant: "destructive" }),
  });

  const sendMutation = useMutation({
    mutationFn: async ({ id, opts }: { id: number; opts: Record<string, unknown> }) => {
      const r = await apiRequest("POST", `/api/app/operations/owner-invoices/${id}/send`, opts);
      return r.json();
    },
    onSuccess: (data: { sendVia: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owner-invoices"] });
      setSendOpen(false);
      setSendInvoice(null);
      toast({ title: "Invoice sent", description: data.sendVia === "mercury" ? "Sent via Mercury AR." : "Sent via email." });
    },
    onError: (e: unknown) => toast({ title: "Send failed", description: parseErrorMessage(e, "Could not send invoice."), variant: "destructive" }),
  });

  const markPaidMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const r = await apiRequest("POST", `/api/app/operations/owner-invoices/${id}/mark-paid`, {});
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owner-invoices"] });
      toast({ title: "Marked as paid" });
    },
    onError: (e: unknown) => toast({ title: "Error", description: parseErrorMessage(e, "Could not mark as paid."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const r = await apiRequest("DELETE", `/api/app/operations/owner-invoices/${id}`, undefined);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owner-invoices"] });
      toast({ title: "Invoice deleted" });
    },
    onError: (e: unknown) => toast({ title: "Delete failed", description: parseErrorMessage(e, "Only draft invoices can be deleted."), variant: "destructive" }),
  });

  const patchNotesMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      const r = await apiRequest("PATCH", `/api/app/operations/owner-invoices/${id}`, { notes });
      return r.json();
    },
    onSuccess: (inv: OwnerInvoice) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owner-invoices"] });
      setDetailInvoice(inv);
      toast({ title: "Notes saved" });
    },
    onError: (e: unknown) => toast({ title: "Error", description: parseErrorMessage(e, ""), variant: "destructive" }),
  });

  const patchRemindersMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, remindersDisabled }: { id: number; remindersDisabled: boolean }) => {
      const r = await apiRequest("PATCH", `/api/app/operations/owner-invoices/${id}`, { remindersDisabled });
      return r.json();
    },
    onSuccess: (inv: OwnerInvoice) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owner-invoices"] });
      toast({ title: inv.remindersDisabled ? "Reminders muted for this invoice" : "Reminders resumed for this invoice" });
    },
    onError: (e: unknown) => toast({ title: "Error", description: parseErrorMessage(e, ""), variant: "destructive" }),
  });

  async function downloadPdf(inv: OwnerInvoice) {
    try {
      await downloadOwnerInvoicePdf({
        invoice: {
          invoiceNumber: inv.invoiceNumber,
          billingMonth: inv.billingMonth,
          dueDate: inv.dueDate,
          notes: inv.notes,
          totalDueCents: inv.totalDueCents,
          lineItems: (inv.lineItems as OwnerInvoiceLineItem[]) ?? [],
        },
        ownerName: owners.find((o) => o.id === inv.ownerId)?.name ?? `Owner #${inv.ownerId}`,
        statusLabel: STATUS_META[inv.status]?.label ?? inv.status,
        invoiceConfig,
        logoSrc: invoiceConfig?.logoUrl ?? null,
      });
    } catch (err) {
      toast({ title: "PDF failed", description: parseErrorMessage(err, "Could not generate PDF."), variant: "destructive" });
    }
  }

  const summaryStats = {
    totalDraft: (invoicesQuery.data ?? []).filter((i) => i.status === "draft").length,
    totalOutstanding: (invoicesQuery.data ?? [])
      .filter((i) => ["sent", "viewed", "overdue"].includes(i.status))
      .reduce((s, i) => s + (i.totalDueCents ?? 0), 0),
    totalPaid: (invoicesQuery.data ?? [])
      .filter((i) => i.status === "paid")
      .reduce((s, i) => s + (i.totalDueCents ?? 0), 0),
  };

  return (
    <AppLayout>
      <BetaAccessGate featureKey="owner_portal" featureName="Owner Portal">
        <div className="p-6 max-w-7xl mx-auto space-y-6">
          {readOnly && <ViewOnlyBanner area="owners" />}

          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1
                className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"
                data-tour="operations-owner-invoices-heading"
              >
                <FileText className="h-6 w-6 text-brand" />
                Owner Invoices
              </h1>
              <p className="text-sm text-gray-500 mt-1">Generate and send monthly management fee invoices to property owners.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ExplainerVideoButton pageKey="operations-owner-invoices" />
              <GuideButton pageKey="operations-owner-invoices" />
              {!readOnly && (
                <Button
                  onClick={() => setGenerateOpen(true)}
                  data-testid="button-generate-invoices"
                  data-tour="operations-owner-invoices-generate"
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Generate Invoices
                </Button>
              )}
            </div>
          </div>

          <GuideNudgeBanner pageKey="operations-owner-invoices" />

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-tour="operations-owner-invoices-summary">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-2">
                  <FileText className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Draft</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="text-draft-count">{summaryStats.totalDraft}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-2">
                  <Send className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Outstanding</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="text-outstanding-amount">{formatCents(summaryStats.totalOutstanding)}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="rounded-full bg-green-100 dark:bg-green-900 p-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Collected</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="text-paid-amount">{formatCents(summaryStats.totalPaid)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList data-tour="operations-owner-invoices-tabs">
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="draft" data-testid="tab-draft">Draft</TabsTrigger>
              <TabsTrigger value="sent" data-testid="tab-sent">Sent</TabsTrigger>
              <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
              <TabsTrigger value="overdue" data-testid="tab-overdue">Overdue</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Table */}
          {invoicesQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">
                {tab === "all"
                  ? `No invoices yet. Click "Generate Invoices" to create the first batch.`
                  : `No ${tab} invoices.`}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[200px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((inv) => {
                    const owner = owners.find((o) => o.id === inv.ownerId);
                    const sm = STATUS_META[inv.status] ?? { label: inv.status, className: "bg-gray-100 text-gray-700" };
                    return (
                      <TableRow key={inv.id} data-testid={`row-invoice-${inv.id}`}>
                        <TableCell className="font-mono text-xs text-gray-700 dark:text-gray-300" data-testid={`text-invoice-number-${inv.id}`}>
                          {inv.invoiceNumber}
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {owner?.name ?? `Owner #${inv.ownerId}`}
                        </TableCell>
                        <TableCell className="text-sm">
                          {billingMonthLabel(inv.billingMonth)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {inv.dueDate ?? "—"}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-sm" data-testid={`text-invoice-amount-${inv.id}`}>
                          {formatCents(inv.totalDueCents)}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sm.className}`} data-testid={`status-invoice-${inv.id}`}>
                            {sm.label}
                          </span>
                          {inv.status === "overdue" && inv.viewedAt && (
                            <div className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400" data-testid={`text-viewed-${inv.id}`}>
                              Viewed {new Date(inv.viewedAt).toLocaleDateString()}
                            </div>
                          )}
                          {inv.status !== "paid" && inv.lastReminderSentAt && (
                            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400" data-testid={`text-last-reminder-${inv.id}`}>
                              Last reminder: {new Date(inv.lastReminderSentAt).toLocaleDateString()}
                            </div>
                          )}
                          {inv.status !== "paid" && inv.remindersDisabled && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400" data-testid={`text-reminders-muted-${inv.id}`}>
                              <BellOff className="h-3 w-3" />
                              Reminders muted
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setDetailInvoice(inv);
                                setNotesEdit(inv.notes ?? "");
                              }}
                              data-testid={`button-review-${inv.id}`}
                            >
                              Review
                            </Button>
                            {!readOnly && inv.status !== "paid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-blue-600"
                                onClick={() => {
                                  setSendInvoice(inv);
                                  setSendMessage("");
                                  setSendUseFallback(false);
                                  setSendOpen(true);
                                }}
                                data-testid={`button-send-${inv.id}`}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Send
                              </Button>
                            )}
                            {!readOnly && inv.status !== "paid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-green-600"
                                onClick={() => markPaidMutation.mutate(inv.id)}
                                disabled={markPaidMutation.isPending}
                                data-testid={`button-mark-paid-${inv.id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Paid
                              </Button>
                            )}
                            {!readOnly && inv.status !== "paid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-gray-500"
                                onClick={() => patchRemindersMutation.mutate({ id: inv.id, remindersDisabled: !inv.remindersDisabled })}
                                disabled={patchRemindersMutation.isPending}
                                title={inv.remindersDisabled ? "Reminders muted — click to resume" : "Mute overdue reminders for this invoice"}
                                data-testid={`button-toggle-reminders-${inv.id}`}
                              >
                                {inv.remindersDisabled ? <BellOff className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => downloadPdf(inv)}
                              data-testid={`button-pdf-${inv.id}`}
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            {!readOnly && inv.status === "draft" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-red-600"
                                onClick={() => {
                                  if (confirm("Delete this draft invoice?")) {
                                    deleteMutation.mutate(inv.id);
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                                data-testid={`button-delete-${inv.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Generate Dialog */}
        <Dialog
          open={generateOpen}
          onOpenChange={(open) => {
            if (generateMutation.isPending) return;
            setGenerateOpen(open);
          }}
        >
          <DialogContent
            onInteractOutside={(e) => { if (generateMutation.isPending) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (generateMutation.isPending) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (generateMutation.isPending) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Generate Owner Invoices</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="gen-month">Billing Month</Label>
                <Input
                  id="gen-month"
                  type="month"
                  value={generateMonth}
                  onChange={(e) => setGenerateMonth(e.target.value)}
                  className="mt-1.5"
                  data-testid="input-generate-month"
                />
                <p className="text-xs text-gray-500 mt-1">Invoices will cover income and costs for this calendar month.</p>
              </div>
              <div>
                <Label htmlFor="gen-owner">Owner</Label>
                <Select value={generateOwnerId} onValueChange={setGenerateOwnerId}>
                  <SelectTrigger id="gen-owner" className="mt-1.5" data-testid="select-generate-owner">
                    <SelectValue placeholder="All owners" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All owners</SelectItem>
                    {owners.map((o) => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Existing drafts for the selected month will be refreshed. Non-draft invoices are skipped.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGenerateOpen(false)} disabled={generateMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const ownerId = generateOwnerId !== "all" ? parseInt(generateOwnerId, 10) : undefined;
                  generateMutation.mutate({ billingMonth: generateMonth, ownerId });
                }}
                disabled={generateMutation.isPending || !generateMonth}
                data-testid="button-confirm-generate"
              >
                {generateMutation.isPending ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Generating…</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" />Generate</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Dialog */}
        <Dialog
          open={sendOpen}
          onOpenChange={(open) => {
            if (sendMutation.isPending) return;
            setSendOpen(open);
          }}
        >
          <DialogContent
            onInteractOutside={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (sendMutation.isPending) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Send Invoice</DialogTitle>
            </DialogHeader>
            {sendInvoice && (
              <div className="space-y-4 py-2">
                <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3 text-sm space-y-1">
                  <p><span className="text-gray-500">Invoice:</span> <span className="font-mono">{sendInvoice.invoiceNumber}</span></p>
                  <p><span className="text-gray-500">Month:</span> {billingMonthLabel(sendInvoice.billingMonth)}</p>
                  <p><span className="text-gray-500">Amount:</span> <strong>{formatCents(sendInvoice.totalDueCents)}</strong></p>
                  <p><span className="text-gray-500">Due:</span> {sendInvoice.dueDate ?? "15th of next month"}</p>
                </div>
                <div className="rounded-md bg-blue-50 dark:bg-blue-950 p-3 text-xs text-blue-700 dark:text-blue-300 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Will send via <strong>Mercury AR</strong> if configured, otherwise falls back to a Resend email with line-item detail. Mercury AR requires the owner's email to be set.</span>
                </div>
                <div>
                  <Label htmlFor="send-message">Message to owner (optional)</Label>
                  <Textarea
                    id="send-message"
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    placeholder="Any notes for the owner…"
                    rows={3}
                    className="mt-1.5"
                    data-testid="input-send-message"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send-fallback"
                    checked={sendUseFallback}
                    onChange={(e) => setSendUseFallback(e.target.checked)}
                    data-testid="checkbox-send-fallback"
                  />
                  <Label htmlFor="send-fallback" className="text-sm font-normal cursor-pointer">
                    Force email fallback (skip Mercury AR even if configured)
                  </Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sendMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!sendInvoice) return;
                  sendMutation.mutate({
                    id: sendInvoice.id,
                    opts: {
                      sendEmail: true,
                      message: sendMessage || undefined,
                      achDebitEnabled: true,
                      creditCardEnabled: false,
                      emailRemindersEnabled: true,
                      useFallbackEmail: sendUseFallback,
                    },
                  });
                }}
                disabled={sendMutation.isPending}
                data-testid="button-confirm-send"
              >
                {sendMutation.isPending ? (
                  <><Send className="h-4 w-4 mr-2 animate-pulse" />Sending…</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Send Invoice</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Review/Detail Dialog — per-property groups can make this tall, so cap + scroll */}
        <Dialog open={!!detailInvoice} onOpenChange={(open) => { if (!open) setDetailInvoice(null); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invoice Details</DialogTitle>
            </DialogHeader>
            {detailInvoice && (() => {
              const inv = detailInvoice;
              const owner = owners.find((o) => o.id === inv.ownerId);
              const sm = STATUS_META[inv.status] ?? { label: inv.status, className: "" };
              const lineItems = (inv.lineItems as OwnerInvoiceLineItem[]) ?? [];
              const lineGroups = groupOwnerInvoiceLineItems(lineItems);
              const summary = inv.portfolioSummary as { propertyCount?: number; propertyNames?: string[]; totalRoomsCount?: number; grossRentCents?: number; expensesCents?: number } | null;
              return (
                <div className="space-y-4 py-1">
                  {invoiceConfig && (
                    <div className="flex items-start gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 p-3" data-testid="invoice-config-header">
                      {invoiceConfig.logoUrl && (
                        <img
                          src={invoiceConfig.logoUrl}
                          alt=""
                          className="h-10 max-w-[120px] object-contain bg-white rounded"
                        />
                      )}
                      <div className="text-xs text-gray-600 dark:text-gray-300 leading-snug">
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{invoiceConfig.companyName}</p>
                        <p>{invoiceConfig.addressLine1}{invoiceConfig.addressLine2 ? `, ${invoiceConfig.addressLine2}` : ""}</p>
                        <p>{[invoiceConfig.city, invoiceConfig.state].filter(Boolean).join(", ")} {invoiceConfig.postalCode}</p>
                        <p>{[invoiceConfig.email, invoiceConfig.phone].filter(Boolean).join(" · ")}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm text-gray-700 dark:text-gray-300">{inv.invoiceNumber}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sm.className}`}>{sm.label}</span>
                  </div>

                  <div className="text-sm space-y-1 text-gray-600 dark:text-gray-400">
                    <p><strong>Owner:</strong> {owner?.name ?? `#${inv.ownerId}`}</p>
                    <p><strong>Month:</strong> {billingMonthLabel(inv.billingMonth)}</p>
                    {inv.dueDate && <p><strong>Due:</strong> {inv.dueDate}</p>}
                    {inv.sentAt && <p><strong>Sent:</strong> {new Date(inv.sentAt).toLocaleDateString()}</p>}
                    {inv.viewedAt && <p data-testid="text-detail-viewed"><strong>Viewed:</strong> {new Date(inv.viewedAt).toLocaleDateString()}</p>}
                    {inv.paidAt && <p><strong>Paid:</strong> {new Date(inv.paidAt).toLocaleDateString()}</p>}
                    {inv.status !== "paid" && inv.lastReminderSentAt && (
                      <p data-testid="text-detail-last-reminder"><strong>Last reminder:</strong> {new Date(inv.lastReminderSentAt).toLocaleDateString()}</p>
                    )}
                    {inv.status !== "paid" && inv.remindersDisabled && (
                      <p className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid="text-detail-reminders-muted">
                        <BellOff className="h-3 w-3" /> Reminders muted for this invoice
                      </p>
                    )}
                    {inv.mercuryInvoiceUrl && (
                      <p><strong>Mercury:</strong> <a href={inv.mercuryInvoiceUrl} target="_blank" rel="noopener noreferrer" className="text-brand underline">View hosted invoice</a></p>
                    )}
                  </div>

                  <Separator />

                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Line Items</p>
                    {lineItems.length === 0 ? (
                      <p className="text-sm text-gray-400">No line items.</p>
                    ) : (
                      <div className="space-y-1">
                        {lineGroups.groups.map((group, gi) => (
                          <div key={group.propertyId ?? `other-${gi}`} className="space-y-1">
                            {lineGroups.grouped && (
                              <div
                                className="flex items-center justify-between text-sm font-semibold text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1 mt-1"
                                data-testid={`invoice-property-group-${group.propertyId ?? "other"}`}
                              >
                                <span>{group.propertyName ?? "Other charges"}</span>
                                <span>{formatCents(group.subtotalCents)}</span>
                              </div>
                            )}
                            {group.items.map((li, i) => (
                              <div key={i} className={`flex items-center justify-between text-sm ${lineGroups.grouped ? "pl-4" : ""}`}>
                                <span className="text-gray-700 dark:text-gray-300">{li.description}</span>
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCents(li.amountCents)}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                        <div className="flex items-center justify-between text-sm font-bold pt-2 border-t mt-2">
                          <span>Total Due</span>
                          <span className="text-brand">{formatCents(inv.totalDueCents)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {summary && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Portfolio Snapshot</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="rounded bg-gray-50 dark:bg-gray-800 p-2">
                            <p className="text-xs text-gray-400">Properties</p>
                            <p className="font-semibold">{summary.propertyCount ?? 0}</p>
                          </div>
                          <div className="rounded bg-gray-50 dark:bg-gray-800 p-2">
                            <p className="text-xs text-gray-400">Bedrooms</p>
                            <p className="font-semibold">{summary.totalRoomsCount ?? 0}</p>
                          </div>
                          <div className="rounded bg-gray-50 dark:bg-gray-800 p-2">
                            <p className="text-xs text-gray-400">Gross Rent</p>
                            <p className="font-semibold">{formatCents(summary.grossRentCents)}</p>
                          </div>
                          <div className="rounded bg-gray-50 dark:bg-gray-800 p-2">
                            <p className="text-xs text-gray-400">Expenses</p>
                            <p className="font-semibold">{formatCents(summary.expensesCents)}</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  <div>
                    <Label htmlFor="detail-notes" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Notes</Label>
                    <Textarea
                      id="detail-notes"
                      value={notesEdit}
                      onChange={(e) => setNotesEdit(e.target.value)}
                      rows={3}
                      placeholder="Add a note for this invoice…"
                      className="mt-1.5"
                      data-testid="input-invoice-notes"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setDetailInvoice(null); }}
              >
                Close
              </Button>
              {!readOnly && detailInvoice && (
                <Button
                  onClick={() => patchNotesMutation.mutate({ id: detailInvoice.id, notes: notesEdit })}
                  disabled={patchNotesMutation.isPending}
                  data-testid="button-save-notes"
                >
                  {patchNotesMutation.isPending ? "Saving…" : "Save Notes"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </BetaAccessGate>
    </AppLayout>
  );
}
